<template>
    <DashboardLayout>
        <div class="flex flex-column" style="min-height: calc(100vh - 100px);">
        <Breadcrumb class="flex breadcrumb" :model="items" />

        <!-- Filters -->
        <div class="card px-3 py-3 mb-0 filter-section">
            <div class="filter-grid">
                <div class="filter-field">
                    <label class="filter-label block mb-1">Grade</label>
                    <Select
                        v-model="selectedGrade"
                        :options="gradeSelectOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select grade"
                        class="w-full filter-control"
                        overlayClass="pp-filter-overlay"
                        size="small"
                        variant="filled"
                        show-clear
                        @change="onGradeChange"
                    />
                </div>
                <div class="filter-field">
                    <label class="filter-label block mb-1">Subject</label>
                    <MultiSelect
                        v-model="selectedSubjects"
                        :options="subjectSelectOptions"
                        :disabled="!selectedGrade || subjectSelectOptions.length === 0"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Select subjects"
                        class="w-full filter-control"
                        overlayClass="pp-filter-overlay"
                        size="small"
                        variant="filled"
                        display="comma"
                        :maxSelectedLabels="2"
                        selectedItemsLabel="{0} subjects selected"
                        show-clear
                    />
                </div>
                <div class="filter-field">
                    <label class="filter-label block mb-1">Year</label>
                    <MultiSelect
                        v-model="selectedYears"
                        :options="yearOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Any year"
                        class="w-full filter-control"
                        overlayClass="pp-filter-overlay"
                        size="small"
                        variant="filled"
                        display="comma"
                        :maxSelectedLabels="2"
                        selectedItemsLabel="{0} years selected"
                        show-clear
                    />
                </div>
                <div class="filter-field" v-if="mediumOptions.length">
                    <label class="filter-label block mb-1">Medium</label>
                    <Select
                        v-model="selectedMedium"
                        :options="mediumOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Any medium"
                        class="w-full filter-control"
                        overlayClass="pp-filter-overlay"
                        size="small"
                        variant="filled"
                        show-clear
                    />
                </div>
                <div class="filter-field">
                    <label class="filter-label block mb-1">Term</label>
                    <Select
                        v-model="selectedTerm"
                        :options="termOptions"
                        optionLabel="label"
                        optionValue="value"
                        placeholder="Any term"
                        class="w-full filter-control"
                        overlayClass="pp-filter-overlay"
                        size="small"
                        variant="filled"
                        show-clear
                    />
                </div>
            </div>

            <div class="filter-footer">
                <div class="filter-toggles">
                    <div class="flex align-items-center gap-2">
                        <Checkbox v-model="onlyWithAnswerSheets" inputId="onlyAnswerSheets" binary @change="applyFilters" />
                        <label for="onlyAnswerSheets" class="Label cursor-pointer">Only papers with answer sheets</label>
                    </div>
                    <div class="flex align-items-center gap-2">
                        <Checkbox v-model="onlyWithMarkingSchemes" inputId="onlyMarkingSchemes" binary @change="applyFilters" />
                        <label for="onlyMarkingSchemes" class="Label cursor-pointer">Only papers with marking schemes</label>
                    </div>
                </div>
                <div class="filter-actions">
                    <Button label="Apply" icon="pi pi-filter" size="small"
                        class="teal-button filter-control filter-btn" @click="applyFilters" />
                    <Button label="Clear" icon="pi pi-times" size="small"
                        class="p-button-outlined filter-control filter-btn"
                        :disabled="!hasActiveFilters" @click="clearFilters" />
                </div>
            </div>
        </div>

        <div ref="resultsRef" class="card p-4">
            <div v-if="gradeGroups.length === 0" class="flex justify-content-center py-8">
                <span class="Caption text-gray-700 text-center">{{ emptyStateText }}</span>
            </div>

            <!-- Results grouped by grade -->
            <div v-else>
                <div v-for="group in gradeGroups" :key="group.grade" class="grade-group">
                    <div class="flex align-items-center gap-2 mb-3">
                        <span class="H6-Medium">{{ cleanupLables(group.grade) }}</span>
                        <span class="grade-count">{{ group.count }} {{ group.count === 1 ? 'paper' : 'papers' }}</span>
                    </div>
                    <div class="paper-grid">
                        <Card v-for="paper in group.papers" :key="paper._id"
                            class="shadow-2 border-round-md paper-card">
                            <template #title>
                                <span class="H6-Medium paper-title truncate block" :title="paper.name">{{ paper.name }}</span>
                            </template>
                            <template #content>
                                <div class="paper-meta">
                                    <div v-for="meta in paperMeta(paper, group.grade)" :key="meta.label" class="paper-meta-row">
                                        <span class="meta-label">{{ meta.label }}</span>
                                        <span class="meta-value truncate" :title="meta.value">{{ meta.value }}</span>
                                    </div>
                                </div>

                                <div class="file-list">
                                    <div v-if="!paper.files?.length" class="Label file-empty">
                                        No files attached yet.
                                    </div>
                                    <div v-for="fileGroup in fileGroups(paper)" :key="fileGroup.kind" class="file-row">
                                        <!-- <i :class="fileGroup.icon" class="file-row-icon"></i> -->
                                        <span class="file-row-label truncate" :title="fileGroup.names.join(', ')">
                                            {{ fileGroup.label }}
                                        </span>
                                        <span v-if="fileGroup.files.length > 1" class="file-row-count">
                                            {{ fileGroup.files.length }}
                                        </span>
                                        <Button icon="pi pi-download" text rounded class="file-action"
                                            :title="fileGroup.downloadHint"
                                            :aria-label="fileGroup.downloadHint"
                                            @click="downloadKind(paper, fileGroup.kind)" />
                                    </div>
                                </div>
                            </template>
                        </Card>
                    </div>
                </div>
            </div>
        </div>

        <Toast />

        <!-- Disclaimer -->
        <div class="text-center w-full max-w-xl mx-auto pt-4 px-6 mt-auto">
            <h3 class="disclaimer-heading text-lg font-semibold mb-2" style="color: #1FC9A3;">Disclaimer</h3>
            <p class="text-700 text-sm m-0">
                All educational materials are for personal use only and may not be copied, distributed, or resold.
                Marking schemes and answer sheets marked "Tutors only" must not be shared with students.
            </p>
        </div>

        <!-- Can't find a past paper -->
            <div class="text-center w-full max-w-xl mx-auto pt-2 pb-4 px-6">
                <p class="text-700 text-sm m-0">
                    Can't find a past paper? Email us at
                    <span class="support-link cursor-pointer px-1" @click="goToContact">support@ilearndsl.com</span>
                    and we'll try our best to upload it.
                </p>
            </div>
        </div>

    </DashboardLayout>
</template>

<script setup>
import { onMounted, onBeforeUnmount, nextTick, ref, computed, watch } from "vue";
import { useRouter } from "vue-router";
import Breadcrumb from 'primevue/breadcrumb';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Toast from 'primevue/toast';
import Select from 'primevue/select';
import MultiSelect from 'primevue/multiselect';
import Checkbox from 'primevue/checkbox';
import DashboardLayout from "@/layout/DashboardLayout.vue";
import { listPapersByGrade, fetchPaperFilters } from "@/api/papers";
import { notifyError, notifyWarn } from "@/utils/notification";
import { cleanupLables, formatSubjectName } from "@/utils/cleanups";
import { downloadPaperFilesOfKind, kindMeta, fileDisplayName, filesOfKind } from "@/utils/paperFiles";
import { getProfile } from "@/api/tutor";
import { useAuthStore } from "@/stores/authStore";


const router = useRouter();
const authStore = useAuthStore();

const items = ref([
    { label: 'Dashboard' },
    { label: 'Past Papers' },
]);

const goToContact = () => {
    router.push({ path: '/aboutUs', query: { scrollTo: 'contact-form' } });
};

const formatSubject = (code) => cleanupLables(formatSubjectName(code));


const teachingLevels = ref([]);
const selectedGrade = ref(null);
const selectedSubjects = ref([]);
const selectedYears = ref([]);
const selectedMedium = ref(null);
const selectedTerm = ref(null);
const onlyWithAnswerSheets = ref(false);
const onlyWithMarkingSchemes = ref(false);

const yearOptions = ref([]);
const mediumOptions = ref([]);
const termOptions = ref([]);

const gradeSubjectMap = computed(() => {
    const map = {};
    teachingLevels.value.forEach(({ grade, subject }) => {
        if (!grade || !subject) return;
        if (!map[grade]) map[grade] = [];
        if (!map[grade].includes(subject)) map[grade].push(subject);
    });
    return map;
});

const gradeSelectOptions = computed(() =>
    Object.keys(gradeSubjectMap.value).map(grade => ({ label: cleanupLables(grade), value: grade }))
);

const subjectSelectOptions = computed(() =>
    (gradeSubjectMap.value[selectedGrade.value] || [])
        .map(code => ({ label: formatSubject(code), value: code }))
);

const hasActiveFilters = computed(() =>
    !!selectedGrade.value ||
    selectedSubjects.value.length > 0 ||
    selectedYears.value.length > 0 ||
    !!selectedMedium.value ||
    !!selectedTerm.value ||
    onlyWithAnswerSheets.value ||
    onlyWithMarkingSchemes.value
);

// Papers state
const gradeGroups = ref([]);
const emptyStateText = ref('Loading papers...');

const onGradeChange = () => {
    selectedSubjects.value = [];
    loadFilterOptions();
};

const clearFilters = () => {
    selectedGrade.value = null;
    selectedSubjects.value = [];
    selectedYears.value = [];
    selectedMedium.value = null;
    selectedTerm.value = null;
    onlyWithAnswerSheets.value = false;
    onlyWithMarkingSchemes.value = false;
    loadFilterOptions();
    fetchPapers();
};

const applyFilters = () => {
    fetchPapers();
};

const loadTeachingLevels = async () => {
    const local = authStore.user?.teachingLevels;
    if (Array.isArray(local) && local.length > 0) {
        teachingLevels.value = local;
        return;
    }
    try {
        const res = await getProfile();
        teachingLevels.value = res.data?.data?.teachingLevels || [];
    } catch {
        teachingLevels.value = [];
    }
};

/**
 * Dropdown values come from the papers that exist, not from a hardcoded list — otherwise a tutor
 * picks a year with nothing behind it and gets an empty page.
 */
const loadFilterOptions = async () => {
    try {
        const res = await fetchPaperFilters(selectedGrade.value ? { grade: selectedGrade.value } : {});
        const data = res.data?.data || {};
        yearOptions.value = (data.years || []).map(y => ({ label: `${y.year}`, value: y.year }));
        mediumOptions.value = (data.mediums || []).map(m => ({ label: m.name, value: m.code }));
        termOptions.value = (data.terms || []).map(t => ({ label: cleanupLables(t.term), value: t.term }));

        // A filter left selected after its option disappeared would silently return nothing.
        if (selectedMedium.value && !mediumOptions.value.some(m => m.value === selectedMedium.value)) {
            selectedMedium.value = null;
        }
        if (selectedTerm.value && !termOptions.value.some(t => t.value === selectedTerm.value)) {
            selectedTerm.value = null;
        }
        selectedYears.value = selectedYears.value.filter(y => yearOptions.value.some(o => o.value === y));
    } catch {
        // Filter options are a convenience; a failure here should not block the paper list.
        yearOptions.value = [];
        mediumOptions.value = [];
        termOptions.value = [];
    }
};

const buildFilters = () => {
    if (selectedGrade.value) {
        const subjects = selectedSubjects.value.length
            ? selectedSubjects.value
            : gradeSubjectMap.value[selectedGrade.value] || [];
        return [{ grade: selectedGrade.value, subjects }];
    }
    return Object.entries(gradeSubjectMap.value)
        .map(([grade, subjects]) => ({ grade, subjects }));
};

const buildExtraFilters = () => {
    const extra = {};
    if (selectedYears.value.length) extra.year = selectedYears.value;
    if (selectedMedium.value) extra.medium = selectedMedium.value;
    if (selectedTerm.value) extra.term = selectedTerm.value;
    if (onlyWithAnswerSheets.value) extra.hasAnswerSheet = true;
    if (onlyWithMarkingSchemes.value) extra.hasMarkingScheme = true;
    return extra;
};

const fetchPapers = async () => {
    const filters = buildFilters();
    if (filters.length === 0) {
        gradeGroups.value = [];
        emptyStateText.value = 'Add the grades and subjects you teach to see relevant past papers.';
        return;
    }

    gradeGroups.value = [];
    emptyStateText.value = 'Loading papers...';
    try {
        const res = await listPapersByGrade(filters, buildExtraFilters());
        const grades = res.data?.data?.grades || [];
        gradeGroups.value = grades.filter(g => g.papers?.length);
        if (gradeGroups.value.length === 0) {
            emptyStateText.value = 'No papers found for the selected filters.';
        }
    } catch (err) {
        gradeGroups.value = [];
        const responseData = err?.response?.data;
        const errorMsg = responseData?.message || "An error occurred while fetching past papers. Please try again.";

        if (err?.response?.status === 403) {
            emptyStateText.value = errorMsg;
            notifyWarn("Subscription Required", errorMsg);
        } else {
            emptyStateText.value = "An error occurred while fetching past papers. Please try again.";
            notifyError(responseData?.status || "Error", errorMsg);
        }
    }
};

// Only the metadata a paper actually has — a blank value is left out rather than rendered
// as a label with nothing after it.
const paperMeta = (paper, grade) => {
    const gradeValue = grade || paper.gradeName || '';
    return [
        { label: 'Grade', value: gradeValue ? cleanupLables(gradeValue) : '' },
        { label: 'Subject', value: paper.subject ? formatSubject(paper.subject) : '' },
        { label: 'Year', value: paper.year ? String(paper.year) : '' },
        { label: 'Term', value: paper.term ? cleanupLables(paper.term) : '' },
        { label: 'Medium', value: paper.medium ? cleanupLables(paper.medium) : '' },
    ].filter(meta => meta.value);
};

const KIND_ORDER = ['paper', 'marking_scheme', 'answer_sheet'];

const fileGroups = (paper) => {
    const files = paper.files || [];
    const kinds = [...new Set([...KIND_ORDER, ...files.map(f => f.kind)])];
    return kinds
        .map(kind => ({ kind, files: filesOfKind(paper, kind) }))
        .filter(group => group.files.length)
        .map(group => {
            const { shortLabel, icon } = kindMeta(group.kind);
            const count = group.files.length;
            const names = group.files.map((file, index) => fileDisplayName(file, index, count));
            return {
                ...group,
                icon,
                names,
                label: count > 1 ? `${shortLabel}s` : shortLabel,
                downloadHint: count > 1 ? `Download all ${count} ${shortLabel.toLowerCase()}s` : `Download ${names[0]}`,
            };
        });
};

const downloadKind = (paper, kind) => downloadPaperFilesOfKind(paper, kind);
const resultsRef = ref(null);

const measureCardHeight = () => {
    const root = resultsRef.value;
    if (!root) return;
    root.style.removeProperty('--paper-card-height');
    const cards = root.querySelectorAll('.paper-card');
    if (!cards.length) return;
    let tallest = 0;
    cards.forEach(card => { tallest = Math.max(tallest, card.offsetHeight); });
    root.style.setProperty('--paper-card-height', `${tallest}px`);
};

let lastWidth = 0;
let resizeObserver = null;

watch(gradeGroups, () => nextTick(measureCardHeight));

onMounted(async () => {
    resizeObserver = new ResizeObserver(entries => {
        const width = entries[0].contentRect.width;
        if (width === lastWidth) return;
        lastWidth = width;
        measureCardHeight();
    });
    if (resultsRef.value) resizeObserver.observe(resultsRef.value);

    await loadTeachingLevels();
    await loadFilterOptions();
    await fetchPapers();
});

onBeforeUnmount(() => {
    resizeObserver?.disconnect();
    resizeObserver = null;
});
</script>

<style scoped>
.breadcrumb {
    display: flex !important;
    flex-wrap: wrap !important;
    overflow-x: hidden !important;
}

.filter-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(12rem, 100%), 1fr));
    align-items: end;
    gap: 0.75rem 1rem;
}

.filter-field {
    min-width: 0;
}

.filter-footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 0.75rem 1.5rem;
    margin-top: 1rem;
}

.filter-toggles {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem 1.5rem;
}

.filter-actions {
    display: flex;
    gap: 0.5rem;
    margin-left: auto;
}

@media (max-width: 575px) {
    .filter-actions {
        width: 100%;
        margin-left: 0;
    }
}

.filter-section .filter-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: var(--p-text-muted-color, #64748b);
    text-transform: uppercase;
    letter-spacing: 0.03em;
}

.filter-section :deep(.filter-control) {
    height: 2.25rem;
    min-height: 2.25rem;
    font-size: 0.8rem !important;
    align-items: center;
}

.filter-section :deep(.p-multiselect-label-container) {
    height: 100%;
    display: flex;
    align-items: center;
}

.filter-section :deep(.p-select-label),
.filter-section :deep(.p-multiselect-label) {
    height: 100%;
    display: flex;
    align-items: center;
    padding-top: 0;
    padding-bottom: 0;
    font-size: 0.8rem !important;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    min-width: 0;
}

.filter-section :deep(.filter-btn) {
    width: 7.5rem;
    flex: 0 0 7.5rem;
    white-space: nowrap;
}

@media (max-width: 575px) {
    .filter-section :deep(.filter-btn) {
        width: auto;
        flex: 1 1 0;
    }
}

.filter-section :deep(.p-button .p-button-label),
.filter-section :deep(.p-button .p-button-icon) {
    font-size: 0.8rem !important;
}

/* Grouped results */
.grade-group:not(:last-child) {
    margin-bottom: 1.5rem;
    padding-bottom: 1rem;
    border-bottom: 1px solid var(--surface-border, #e2e8f0);
}

.grade-count {
    font-size: 0.75rem;
    color: var(--p-text-muted-color, #64748b);
    background: var(--surface-100, #f1f5f9);
    border-radius: 1rem;
    padding: 0.15rem 0.6rem;
}

/* Equal-height cards */
.paper-grid {
    display: grid;
    grid-template-columns: 1fr;
    grid-auto-rows: 1fr;
    gap: 1rem;
}

@media (min-width: 576px) {
    .paper-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }
}

@media (min-width: 992px) {
    .paper-grid {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }
}

@media (min-width: 1200px) {
    .paper-grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }
}

.paper-card {
    padding: 1.15rem 1.25rem;
    height: 100%;
    min-height: var(--paper-card-height, auto);
    min-width: 0;
    display: flex;
    flex-direction: column;
    transition: transform 0.2s ease, box-shadow 0.2s ease;
    background-color: var(--surface-card);
}

.paper-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 14px rgba(0, 0, 0, 0.08);
}

.paper-card :deep(.p-card-body),
.paper-card :deep(.p-card-content) {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.paper-card :deep(.p-card-caption) {
    flex: 0 0 auto;
}

.paper-title {
    font-size: 1.2rem;
    line-height: 1.5;
    font-weight: 500;;
}

.paper-meta {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    flex: 0 0 auto;
    padding: 0.15rem 0.25rem 0;
}

.paper-meta-row {
    display: grid;
    grid-template-columns: 1fr 1fr;
    align-items: baseline;
    min-width: 0;
}

.meta-label {
    min-width: 0;
    font-size: 0.72rem;
    font-weight: 600;
    line-height: 1.35;
    color: var(--gray-700);
    text-transform: uppercase;
    letter-spacing: 0.02em;
}

.meta-value {
    min-width: 0;
    font-size: 0.8rem;
    line-height: 1.35;
    color: var(--gray-600);
}

.file-list {
    display: flex;
    flex-direction: column;
    gap: 0.1rem;
    margin-top: auto;
    padding-top: 0.6rem;
    flex: 0 1 auto;
    min-height: 0;
    overflow-y: auto;
}

.file-empty {
    color: var(--gray-700);
    font-size: 0.8rem;
    padding: 0.35rem 0.25rem;
}

.file-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.2rem 0.25rem;
    border-radius: 6px;
    min-height: 2rem;
}

.file-row-icon {
    font-size: 0.85rem;
    color: var(--gray-700);
    flex: 0 0 auto;
}

.file-row-label {
    font-size: 0.8rem;
    font-weight: 500;
    color: var(--gray-600);
    flex: 1 1 auto;
    min-width: 0;
}

.file-row-count {
    flex: 0 0 auto;
    font-size: 0.7rem;
    line-height: 1;
    color: var(--gray-600);
    background: var(--gray-200, #f3f4f6);
    border-radius: 1rem;
    padding: 0.2rem 0.4rem;
}

.file-list :deep(.file-action) {
    width: 1.75rem;
    height: 1.75rem;
    padding: 0;
    flex: 0 0 auto;
    margin-left: auto;
    color: var(--gray-600);
}

.file-list :deep(.file-action:hover),
.file-list :deep(.file-action:focus-visible) {
    color: var(--gray-600);
    background: var(--gray-300, #dbdfe3);
}

.file-list :deep(.file-action .p-button-icon) {
    font-size: 0.85rem;
}


.support-link {
    color: #26b697;
    text-decoration: underline;
}

.support-link:hover {
    color: #25d8b1;
}

.text-gray-700 {
    color: var(--gray-700) !important;
}

.truncate {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>

<style>
.pp-filter-overlay .p-select-option,
.pp-filter-overlay .p-multiselect-option {
    font-size: 0.8rem;
    padding-top: 0.4rem;
    padding-bottom: 0.4rem;
}
</style>
